import { memo, useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
// Import Types
import type { UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import UI components
import { Button } from '@geneui/components';
// Import Components
import DataTable from 'components/common/DataTable';
import AdminEmailProviderActionsButtons from './AdminEmailProviderActionsButtons';
import AdminProviderActionsModals from './AdminProviderActionsModals';
// Import Hooks
import useToaster from 'hooks/UseToaster';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { AdministrationHttpService } from 'services/http';
// Import Configs
import { DataTableColumnsConfigs } from './config';
// Import Constants
import { l } from 'constants/common';

interface Props {
    tableKey?: string;
    listFilters: UseListFiltersReturnValue;
}

interface DataTableRef {
    resetSelectedRows?: () => void;
    reset?: () => void;
}

const AdminProviderList = forwardRef<DataTableRef | null, Props>(
    ({ tableKey = 'adminProvidersDataTable', listFilters }, ref) => {
        const { t } = useTranslation();
        const { showToaster } = useToaster();
        const { doPostRequest } = useRequest();

        const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

        const [isProviderListLoading, setIsProviderListLoading] = useState(false);

        const [providerListData, setProviderListData] = useState([]);
        const [providerListDataCount, setProviderListDataCount] = useState(0);

        const [isEditModalOpenedState, setIsEditModalOpenedState] = useState(false);
        const [isCreateModalOpenedState, setIsCreateModalOpenedState] = useState(false);
        const [isCloneModalOpenedState, setIsCloneModalOpenedState] = useState(false);
        const [openedModalData, setOpenedModalData] = useState({});

        const { columns } = DataTableColumnsConfigs(t);
        const dataTableRef = useRef<DataTableRef>(null);

        const createProviderAction = (providerListData) => {
            setOpenedModalData(providerListData);
            setIsCreateModalOpenedState(true);
        };

        const getHeaderActions = () => {
            return (
                <Button
                    flexibility="default"
                    icon="bc-icon-profit"
                    onClick={() => createProviderAction(providerListData)}
                >
                    {t(l.CreateProvider)}
                </Button>
            );
        };

        const { getEmailProviderListRequest } = useCancelableRequests(
            { getEmailProviderListRequest: AdministrationHttpService.getEmailProviderList },
            { takeLatest: ['getEmailProviderListRequest'] },
        );

        const getProviderList = useCallback(
            (filter = baseRequestBody) => {
                setIsProviderListLoading(true);
                doPostRequest(getEmailProviderListRequest.request, {
                    requestBody: filter,
                    successCallback: ({ Data, Count }) => {
                        const rows = getTableRows(Data || []);
                        setProviderListData(rows);
                        setProviderListDataCount(Count);
                    },
                })?.then(({ IsCanceled }) => {
                    // A superseded call must not hide the loader of the call that replaced it
                    if (!IsCanceled) {
                        setIsProviderListLoading(false);
                    }
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [baseRequestBody],
        );

        const editProviderAction = (provider) => {
            setOpenedModalData(provider);
            setIsEditModalOpenedState(true);
        };

        const cloneProviderAction = (provider) => {
            setOpenedModalData(provider);
            setIsCloneModalOpenedState(true);
        };

        const modalsStateSetDict = {
            createProvider: {
                key: 'createProvider',
                fn: setIsCreateModalOpenedState,
            },
            editProvider: {
                key: 'editProvider',
                fn: setIsEditModalOpenedState,
            },
            cloneProvider: {
                key: 'cloneProvider',
                fn: setIsCloneModalOpenedState,
            },
        };

        const getRowActionBar = (row) => {
            const { data } = row;

            return (
                <>
                    <AdminEmailProviderActionsButtons
                        data={data}
                        editProviderAction={editProviderAction}
                        cloneProviderAction={cloneProviderAction}
                    />
                </>
            );
        };

        const getTableColumns = () => {
            return columns;
        };

        const getTableRows = (providerList) => {
            return providerList.map(
                ({
                    EmailProviderId,
                    Name,
                    Description,
                    Host,
                    Port,
                    Type,
                    UserName,
                    FromAddress,
                    PartnerEmail,
                    MaxDegreeOfParallelism,
                    DelayTimout,
                    Partners,
                    IsDefault,
                    EnableSsl,
                    NotInjectUnsubscribeLink,
                }) => {
                    return {
                        hasHover: true,
                        dragDisable: true,
                        nestedData: [],
                        data: {
                            rowKey: EmailProviderId,
                            isRowOpend: false,
                            EmailProviderId,
                            Name,
                            Description,
                            Host,
                            Port,
                            Type,
                            UserName,
                            FromAddress,
                            PartnerEmail,
                            MaxDegreeOfParallelism,
                            DelayTimout,
                            Partners,
                            IsDefault,
                            EnableSsl,
                            NotInjectUnsubscribeLink,
                        },
                    };
                },
            );
        };

        const getTableRefreshFn = () => {
            getProviderList(baseRequestBody);
        };

        const onCloseModalHandler = ({ modalStateKey, alertType, alertMessage, isDoRefresh = false }) => {
            modalsStateSetDict[modalStateKey].fn(false);

            if (isDoRefresh) {
                showToaster(alertType, alertMessage);
                getTableRefreshFn();
            }
        };

        useImperativeHandle(ref, () => ({
            resetSelectedRows() {
                dataTableRef.current?.resetSelectedRows?.();
            },
            reset() {
                dataTableRef.current?.reset?.();
            },
        }));

        useEffect(() => {
            if (isMounted.current) {
                getProviderList(baseRequestBody);
            }
            /* eslint-disable react-hooks/exhaustive-deps */
        }, [baseRequestBody]);

        return (
            <>
                <DataTable
                    ref={dataTableRef}
                    classNames="with-filter-tags-and-page-headline-height"
                    name={l.EmailProviders}
                    rowKey="rowKey"
                    columnKey="dataKey"
                    tableKey={tableKey}
                    isColumnsSortable={true}
                    data={providerListData}
                    dataCount={providerListDataCount}
                    rowActionBar={getRowActionBar}
                    columns={getTableColumns()}
                    isLoading={isProviderListLoading}
                    isShowRefreshButton={true}
                    isShowColumnChooser={true}
                    setPagingPageSize={changePageSize}
                    setPagingPageNumber={changePageNumber}
                    sorting={baseRequestBody.Sorting}
                    setSorting={changeSorting}
                    onRefreshClick={getTableRefreshFn}
                    headerActions={getHeaderActions()}
                    isHasArchiveActions={false}
                    isHasUnarchiveActions={false}
                    receivedCurrentPageNumber={baseRequestBody.Pageing.PageNumber}
                    withQueryParams={true}
                />
                <AdminProviderActionsModals
                    isCreateModalOpenedState={isCreateModalOpenedState}
                    isEditModalOpenedState={isEditModalOpenedState}
                    isCloneModalOpenedState={isCloneModalOpenedState}
                    onCloseModalHandler={onCloseModalHandler}
                    modalsStateSetDict={modalsStateSetDict}
                    openedModalData={openedModalData}
                />
            </>
        );
    },
);

AdminProviderList.displayName = 'AdminProviderList';

export default memo(AdminProviderList);
